import React, { useEffect, useRef, useState } from 'react';
import { Camera, Eye, PenLine, Upload, X } from 'lucide-react';
import SignatureCapture from './SignatureCapture';

function readJournal(key) {
  try {
    if (!key) return { state: 'blocked' };
    const record = JSON.parse(sessionStorage.getItem(key) || 'null');
    if (record && !(['pending', 'uploaded'].includes(record.state) && typeof record.filename === 'string'
      && (record.state !== 'uploaded' || typeof record.id === 'string'))) return { state: 'blocked' };
    return record;
  } catch { return { state: 'blocked' }; }
}

export default function Evidence({ receiptId, lineIndex, version, ids, savedIds = [], journalKey, api, active, editable, disabled, onUploaded, onBusy }) {
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState('');
  const [classification, setClassification] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [journal, setJournal] = useState(() => readJournal(journalKey));
  const [preview, setPreview] = useState(null);
  const [inputKey, setInputKey] = useState(0);
  const [showSignature, setShowSignature] = useState(false);
  const alive = useRef(false), lock = useRef(false), url = useRef(null);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; if (url.current) URL.revokeObjectURL(url.current); };
  }, []);
  useEffect(() => { setNotice(''); }, [version]);
  useEffect(() => {
    if (journal?.state === 'uploaded' && savedIds.includes(journal.id)) {
      try { sessionStorage.removeItem(journalKey); setJournal(null); }
      catch { setError('Could not clear the saved upload record.'); }
    }
  }, [journal, journalKey, savedIds]);
  const current = () => alive.current && active();
  function record(value) {
    try {
      if (value) sessionStorage.setItem(journalKey, JSON.stringify(value));
      else sessionStorage.removeItem(journalKey);
      setJournal(value); return true;
    } catch { setError('Upload recovery storage is unavailable. No new upload will be sent.'); setJournal({ state: 'blocked' }); return false; }
  }
  function close() {
    if (url.current) URL.revokeObjectURL(url.current);
    url.current = null; setPreview(null);
  }
  async function upload(event) {
    event.preventDefault();
    if (!current() || lock.current || disabled || !editable || journal || !file) return;
    if (!category.trim() || !classification || ids.length >= 20 || file.size === 0 || file.size > 50 * 1024 * 1024) {
      setError('Choose a nonempty file up to 50 MB, category and classification. A line allows 20 files.'); return;
    }
    if (!record({ state: 'pending', filename: file.name })) return;
    lock.current = true; setBusy(true); onBusy(true); setError(''); setNotice('');
    try {
      const result = await api.uploadEvidence(receiptId, lineIndex, file, { category, classification });
      if (!current()) return;
      if (!result?.id) throw new Error('Upload returned no document reference.');
      if (!record({ state: 'uploaded', filename: file.name, id: result.id })) return;
      onUploaded(result.id); setFile(null); setInputKey(key => key + 1); setNotice('Uploaded. Save the line to attach this evidence.');
    } catch (e) {
      if (!current()) return;
      const rejected = [400, 401, 403, 404, 413, 415, 422].includes(e.status);
      if (rejected) record(null);
      setError(rejected ? e.message : `${e.message} The file may already be uploaded. No automatic retry was made.`);
    } finally { lock.current = false; if (current()) { setBusy(false); onBusy(false); } }
  }
  async function view(id) {
    if (!current() || lock.current || disabled) return;
    lock.current = true; setBusy(true); onBusy(true); setError(''); close();
    try {
      const blob = await api.evidencePreview(id);
      if (!current()) return;
      const image = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'].includes(blob.type);
      if (!image && blob.type !== 'application/pdf') throw new Error('Preview supports image and PDF evidence only.');
      if (image) url.current = URL.createObjectURL(blob);
      setPreview({ url: url.current, image, id });
    } catch (e) { if (current()) setError(e.message); }
    finally { lock.current = false; if (current()) { setBusy(false); onBusy(false); } }
  }
  async function download(id) {
    if (!current() || lock.current || disabled) return;
    lock.current = true; setBusy(true); onBusy(true); setError('');
    try {
      const blob = await api.evidenceDownload(id);
      if (!current()) return;
      if (blob.type !== 'application/pdf') throw new Error('The document is not a PDF.');
      if (url.current) URL.revokeObjectURL(url.current);
      url.current = URL.createObjectURL(blob); setPreview({ url: url.current, image: false, id });
    } catch (e) { if (current()) setError(e.message); }
    finally { lock.current = false; if (current()) { setBusy(false); onBusy(false); } }
  }
  function choose(event) { setFile(event.target.files?.[0] || null); setNotice(''); }
  return <section className="receiving-arrival" aria-label="Line evidence">
    <h2>Line evidence</h2>
    {error && <p role="alert" className="receiving-error">{error}</p>}
    {notice && <p role="status">{notice}</p>}
    {journal?.state === 'pending' && <p role="alert" className="receiving-error">Unconfirmed upload: {journal.filename}. The file may already exist. Do not upload it again without checking.</p>}
    {journal?.state === 'blocked' && <p role="alert" className="receiving-error">Upload recovery record is unavailable or invalid. Uploads are blocked.</p>}
    {journal?.state === 'uploaded' && <p role="status">Uploaded file awaiting line attachment: {journal.filename}</p>}
    {editable && journal?.state === 'uploaded' && !ids.includes(journal.id) && <button type="button" disabled={busy || disabled || ids.length >= 20}
      onClick={() => { if (current()) onUploaded(journal.id); }}>Attach uploaded evidence</button>}
    <ul className="receiving-evidence-list">{ids.map((id, i) => <li key={id}><span>Evidence {i + 1}</span><button type="button" disabled={busy || disabled} title={`Preview evidence ${i + 1}`} aria-label={`Preview evidence ${i + 1}`} onClick={() => view(id)}><Eye size={18} /></button></li>)}</ul>
    {preview && <div className="receiving-preview"><button type="button" disabled={busy} title="Close preview" aria-label="Close preview" onClick={close}><X size={18} /></button>
      {preview.image ? <img src={preview.url} alt="Receipt line evidence" /> : preview.url
        ? <a href={preview.url} download={`evidence-${preview.id}.pdf`}>Save PDF evidence</a>
        : <button type="button" disabled={busy || disabled} onClick={() => download(preview.id)}>Download PDF evidence</button>}
    </div>}
    {editable && <form aria-label="Upload line evidence" onSubmit={upload}>
      <fieldset disabled={busy || disabled || !!journal}>
        <label><Camera size={18} />Camera photo<input key={`camera-${inputKey}`} type="file" accept="image/*" capture="environment" onChange={choose} /></label>
        <label>Evidence file<input key={`file-${inputKey}`} type="file" accept="image/png,image/jpeg,image/webp,image/gif,application/pdf" onChange={choose} /></label>
        <label>Document category<input required maxLength={200} value={category} onChange={e => setCategory(e.target.value)} /></label>
        <label>Classification<select required value={classification} onChange={e => setClassification(e.target.value)}><option value="">Select classification</option>{['L1', 'L2', 'L3', 'L4'].map(level => <option key={level} value={level}>{level}</option>)}</select></label>
      </fieldset>
      {file && <p>{file.name}</p>}
      <button disabled={busy || disabled || !!journal || !file || !category.trim() || !classification || ids.length >= 20} type="submit"><Upload size={18} />Upload evidence</button>
      <button type="button" disabled={busy || disabled || !!journal || ids.length >= 20} onClick={() => setShowSignature(value => !value)}><PenLine size={18}/>{showSignature ? 'Close signature' : 'Driver signature'}</button>
      {journal?.state === 'pending' && <button type="button" disabled={busy || disabled} onClick={() => { if (current() && window.confirm('An earlier upload may exist. Uploading again can create a duplicate. Continue?') && record(null)) { setFile(null); setInputKey(key => key + 1); setError(''); } }}>Choose another file</button>}
    </form>}
    {editable && showSignature && <SignatureCapture disabled={busy || disabled || !!journal} onFile={(signature) => { setFile(signature); setCategory('Operations'); setClassification('L1'); setShowSignature(false); setNotice('Signature image prepared. Upload it, then save the line.'); }} />}
  </section>;
}
